import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-success/10 text-success',
    paused: 'bg-warning/10 text-warning',
    draft: 'bg-muted text-muted-foreground',
    completed: 'bg-primary/10 text-primary',
    cancelled: 'bg-error/10 text-error',
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${map?.[status] || 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
};

const CampaignDetailModal = ({ campaign, onClose, onUpdateStatus }) => {
  const [newStatus, setNewStatus] = useState(campaign?.status);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdateStatus(campaign?.id, newStatus);
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">Campaign Details</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-lg font-semibold text-foreground">{campaign?.title}</p>
            <p className="text-sm text-muted-foreground">{campaign?.brandName}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Budget', value: `$${parseFloat(campaign?.budget || 0)?.toFixed(0)}`, icon: 'DollarSign' },
              { label: 'Spent', value: `$${parseFloat(campaign?.spent || 0)?.toFixed(0)}`, icon: 'TrendingUp' },
              { label: 'Reward/Post', value: `$${parseFloat(campaign?.rewardAmount || 0)?.toFixed(2)}`, icon: 'Gift' },
              { label: 'Submissions', value: campaign?.submissionCount || 0, icon: 'FileText' },
            ]?.map((s, i) => (
              <div key={i} className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name={s?.icon} size={14} color="var(--color-primary)" />
                  <span className="text-xs text-muted-foreground">{s?.label}</span>
                </div>
                <p className="text-base font-bold text-foreground">{s?.value}</p>
              </div>
            ))}
          </div>
          {campaign?.description && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description</p>
              <p className="text-sm text-foreground">{campaign?.description}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Start Date</p>
              <p className="text-sm text-foreground">{campaign?.startDate || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">End Date</p>
              <p className="text-sm text-foreground">{campaign?.endDate || '—'}</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Update Status</p>
            <Select
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'active', label: 'Active' },
                { value: 'paused', label: 'Paused' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
              value={newStatus}
              onChange={setNewStatus}
            />
          </div>
          <Button variant="default" size="sm" onClick={handleSave} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CampaignManagement = ({ currentAdminId }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('campaigns')
        ?.select(`id, title, description, budget, spent, reward_amount, status, start_date, end_date, created_at, brand:user_profiles!campaigns_brand_id_fkey(full_name, email)`)
        ?.order('created_at', { ascending: false });
      if (!error && data) {
        const enriched = await Promise.all(
          data?.map(async (c) => {
            const { count } = await supabase?.from('transactions')?.select('*', { count: 'exact', head: true })?.eq('campaign_id', c?.id);
            return {
              id: c?.id,
              title: c?.title,
              description: c?.description,
              brandName: c?.brand?.full_name || c?.brand?.email?.split('@')?.[0] || 'Brand',
              budget: c?.budget,
              spent: c?.spent,
              rewardAmount: c?.reward_amount,
              status: c?.status || 'draft',
              startDate: c?.start_date ? new Date(c?.start_date)?.toLocaleDateString() : null,
              endDate: c?.end_date ? new Date(c?.end_date)?.toLocaleDateString() : null,
              submissionCount: count || 0,
              createdAt: new Date(c?.created_at)?.toLocaleDateString(),
            };
          })
        );
        setCampaigns(enriched);
      }
    } catch (err) {
      console.error('[CampaignManagement] loadCampaigns error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleUpdateStatus = async (campaignId, newStatus) => {
    try {
      const { error } = await supabase?.from('campaigns')?.update({ status: newStatus })?.eq('id', campaignId);
      if (!error) {
        setCampaigns(prev => prev?.map(c => c?.id === campaignId ? { ...c, status: newStatus } : c));
        await supabase?.from('moderation_logs')?.insert({
          moderator_id: currentAdminId,
          campaign_id: campaignId,
          action: 'reviewed',
          notes: `Campaign status changed to ${newStatus} by admin`,
        });
      }
    } catch (err) {
      console.error('[CampaignManagement] handleUpdateStatus error:', err);
    }
  };

  const filtered = campaigns?.filter(c => {
    const matchSearch = c?.title?.toLowerCase()?.includes(search?.toLowerCase()) || c?.brandName?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || c?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-5">
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">All Campaigns</h3>
            <p className="text-sm text-muted-foreground">{filtered?.length} campaigns</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-48"><Input type="search" placeholder="Search campaigns..." value={search} onChange={(e) => setSearch(e?.target?.value)} /></div>
            <div className="w-36">
              <Select
                options={[{ value: 'all', label: 'All Status' }, { value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }, { value: 'draft', label: 'Draft' }, { value: 'completed', label: 'Completed' }, { value: 'cancelled', label: 'Cancelled' }]}
                value={statusFilter} onChange={setStatusFilter}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {['Campaign', 'Brand', 'Reward/Post', 'Budget', 'Spent', 'Submissions', 'Status', 'Actions']?.map(h => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered?.map((c) => (
                  <tr key={c?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-3">
                      <p className="text-sm font-medium text-foreground max-w-[160px] truncate">{c?.title}</p>
                      <p className="text-xs text-muted-foreground">{c?.createdAt}</p>
                    </td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground">{c?.brandName}</span></td>
                    <td className="py-3 px-3"><span className="text-sm font-semibold text-primary">${parseFloat(c?.rewardAmount || 0)?.toFixed(2)}</span></td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground">${parseFloat(c?.budget || 0)?.toFixed(0)}</span></td>
                    <td className="py-3 px-3"><span className="text-sm text-success">${parseFloat(c?.spent || 0)?.toFixed(0)}</span></td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground">{c?.submissionCount}</span></td>
                    <td className="py-3 px-3"><StatusBadge status={c?.status} /></td>
                    <td className="py-3 px-3">
                      <button onClick={() => setSelectedCampaign(c)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View/Edit">
                        <Icon name="Edit" size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No campaigns found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selectedCampaign && (
        <CampaignDetailModal
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </div>
  );
};

export default CampaignManagement;
